/**
 * Pure constants + presentational helpers for {@link ChangeRequestsTab},
 * extracted so the tab component stays under the 500-line file limit.
 */
import { Edit, CheckCircle, XCircle, Clock, Calendar, Package, DollarSign } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ChangelogEntry } from '../common/Changelog';

/** The three per-field change kinds a supplier/customer can propose. */
export type ChangeField = 'date' | 'quantity' | 'price';

// CR-7166: Added delivery_date for customer-raised date changes.
export const CHANGE_FIELDS = new Set([
  'supplier_promise_date',
  'delivery_date',
  'quantity',
  'unit_price',
]);

const CHANGE_STATUS_VALUES = new Set([
  'change_proposed_supplier',
  'change_proposed_customer',
  'accepted',
  'change_declined',
]);

// CR-7166: Added delivery_date label for customer-raised date changes.
export const FIELD_LABELS: Record<string, string> = {
  supplier_promise_date: 'Promise Date',
  delivery_date: 'Required Date',
  quantity: 'Quantity',
  unit_price: 'Unit Price',
  acknowledge_status: 'Status',
};

// CR-7166: Added delivery_date icon for customer-raised date changes.
export const FIELD_ICONS: Record<string, LucideIcon> = {
  supplier_promise_date: Calendar,
  delivery_date: Calendar,
  quantity: Package,
  unit_price: DollarSign,
  acknowledge_status: Edit,
};

export const STATUS_DISPLAY: Record<string, string> = {
  change_proposed_supplier: 'Proposed by Supplier',
  change_proposed_customer: 'Proposed by Customer',
  accepted: 'Accepted',
  change_declined: 'Declined',
};

export function getStatusBadge(status: string) {
  const config: Record<string, { label: string; className: string; icon: LucideIcon }> = {
    open: { label: 'Open - Awaiting Decision', className: 'bg-orange-100 text-orange-800 border-orange-200', icon: Clock },
    // CR-4863 round 2: History rows describing the original proposal event
    // use this neutral past-tense badge so they do not keep claiming the
    // request is "Awaiting Decision" after it has been accepted/rejected.
    // The live "Open - Awaiting Decision" badge is reserved for the Active
    // Change Request card, which auto-hides when the line is resolved.
    proposed: { label: 'Proposed', className: 'bg-orange-100 text-orange-800 border-orange-200', icon: Edit },
    accepted: { label: 'Accepted', className: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
    // CR-4863: name the status "Rejected" rather than the legacy "Declined"
    // so the audit-trail label matches the action button users clicked
    // ("Reject"). Spec calls it out explicitly.
    declined: { label: 'Rejected', className: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
    // CR-4864: surface "Cancelled" alongside the other terminal states so
    // proposer-cancelled rows in Change History don't fall through to the
    // open / "no badge" paths.
    cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-700 border-gray-200', icon: XCircle },
  };
  const c = config[status] || config['open'];
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${c.className}`}>
      <Icon className="h-3 w-3" />
      {c.label}
    </span>
  );
}

export function isChangeRelatedStatusEntry(entry: ChangelogEntry): boolean {
  if (entry.field_changed !== 'acknowledge_status') return false;
  const oldVal = entry.old_value || '';
  const newVal = entry.new_value || '';
  return CHANGE_STATUS_VALUES.has(oldVal) || CHANGE_STATUS_VALUES.has(newVal);
}

export function getHistoryStatusBadge(entry: ChangelogEntry) {
  // Acknowledge-status field changes carry the status verbatim in new_value.
  if (entry.field_changed === 'acknowledge_status') {
    const newVal = entry.new_value;
    if (newVal === 'accepted') return getStatusBadge('accepted');
    if (newVal === 'change_declined') return getStatusBadge('declined');
    // CR-4863 round 2: proposal history rows render as "Proposed" (past
    // tense). "Open - Awaiting Decision" only makes sense for the live
    // Active Change Request card — once a history row is written, the
    // event it describes has already happened.
    if (newVal === 'change_proposed_supplier' || newVal === 'change_proposed_customer') return getStatusBadge('proposed');
  }
  // CR-4864: per-field rows (Promise Date, Quantity, Unit Price) don't
  // have a status field of their own — derive their outcome from the
  // event_type and reason text the workflow service writes, so users can
  // tell at a glance whether each historical row was accepted, rejected,
  // cancelled, or still open. Keeps the badge vocabulary consistent with
  // the Active Change Request panel above.
  const reason = (entry.reason || '').toLowerCase();
  if (entry.event_type === 'change_accepted' || /\baccepted\b/.test(reason)) {
    return getStatusBadge('accepted');
  }
  if (/\b(rejected|declined)\b/.test(reason)) {
    return getStatusBadge('declined');
  }
  if (/\bcancelled\b/.test(reason)) {
    return getStatusBadge('cancelled');
  }
  if (entry.event_type === 'change_request' || /\bproposed\b/.test(reason)) {
    return getStatusBadge('proposed');
  }
  return null;
}
